import { AppStack, Map } from '@/App.tsx'
import '@/generated/assets'
import { assets, onAssetChange } from '@/generated/assets'
import '@/index.scss'
import {
	preloadModule,
	preloadModules,
	type RegistryListener,
} from '@/registry'
import { setDefaultAsset, setDefaultAssets } from '@/stores/assets'
import { metaRender, metaSsrHydrate } from '@/stores/meta'
import { createStore, type State } from '@/stores/store'
import { PriorityExecutor } from '@lc/librunner'
import { createRoot, hydrateRoot } from 'react-dom/client'
import { useBrowserLocation, useSearch } from 'wouter/use-browser-location'

// inject our polyfills if we're in live mode
if (process.env.LIVE) {
	await import(/* webpackMode: 'eager' */ '@/polyfill')
}

// get our target root
const root = document.querySelector('#root')!

// create and assign our executor
const executor = new PriorityExecutor({ maxLevel: 4, concurrencyLimit: 5 })
Object.assign(window, { executor })
executor.onSchedule(task => {
	console.log(`[exe] Schedule ${task.name} at level ${task.level}`)
})
executor.onRun(task => {
	console.log(`[exe] Run ${task.name}`)
})

// load SSR data if we have any
const ssrScript = document.querySelector('#ssr-data')
const ssrData: {
	path: string
	modules: string[]
	state: State
	lang: string
	langs: string[]
} | null = ssrScript ? JSON.parse(ssrScript.innerHTML) : null

// if we're running in SSR mode, load our modules
// we need to do this before even loading the store because
// our preloaded state contains data from slices contained there
if (ssrData) {
	const dummyStore = createStore()
	await Promise.all(
		ssrData.modules.map(module => {
			return preloadModule(dummyStore, executor, module, 0)
		}),
	)
}

// create and assign our store
const store = createStore(ssrData?.state)
Object.assign(window, { store })
Object.defineProperty(window, 'state', { get: () => store.getState() })

// in live mode, update assets on the fly
if (process.env.LIVE) {
	const setChangeHandler = () => {
		onAssetChange((id, path) => {
			console.log('[hot] Updated asset', id, path)
			store.dispatch(setDefaultAsset([id, path]))
		})
	}
	setChangeHandler()
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	;(import.meta as any).webpackHot.accept('@/generated/assets', () => {
		console.log('[hot] Updated assets', assets)
		setChangeHandler()
		store.dispatch(setDefaultAssets(assets))
	})
}

// list the modules we're actually using
let collectModules: RegistryListener = () => void 0

// in live mode, do some local dev optimizations
if (process.env.LIVE) {
	void Map.preload({ executor, store, level: 0 })

	setTimeout(() => {
		void preloadModules(store, executor, 4)
	}, 1000)

	const usedModules = new Set<string>()
	collectModules = name => {
		if (usedModules.has(name)) return
		usedModules.add(name)
		console.log(`[info] Using module ${name}`)
	}
}

// render or hydrate our app
if (ssrData) {
	try {
		store.dispatch(metaSsrHydrate(new Date().toISOString()))
		hydrateRoot(
			root,
			<AppStack
				store={store}
				executor={executor}
				preloadedState={ssrData.state}
				ssrPath={ssrData.path}
				ssrLang={ssrData.lang}
				ssrLangs={ssrData.langs}
				collectModules={collectModules}
				locationHook={useBrowserLocation}
				searchHook={useSearch}
			/>,
		)
	} catch (e) {
		console.error('[ssr] Hydration failed', e)
		store.dispatch(metaRender([new Date().toISOString(), 'app']))
		createRoot(root).render(
			<AppStack
				store={store}
				executor={executor}
				collectModules={collectModules}
				locationHook={useBrowserLocation}
				searchHook={useSearch}
			/>,
		)
	}
} else {
	store.dispatch(metaRender([new Date().toISOString(), 'app']))
	createRoot(root).render(
		<AppStack
			store={store}
			executor={executor}
			collectModules={collectModules}
			locationHook={useBrowserLocation}
			searchHook={useSearch}
		/>,
	)
}
